import React from "react";
import { Card, CardContent, CardHeader, TextField, Box, Typography, Container } from "@mui/material";
import TimelineIcon from '@mui/icons-material/Timeline';

interface QuickQuotePanelProps {
    numberOfVehicles: number;
    chargesPerWeek: number;
    averageChargeSize: number;
    monthlyRequirementKWh: number;
    onQuoteChange: (updatedValues: any) => void;
}

const QuickQuotePanel: React.FC<QuickQuotePanelProps> = ({
    numberOfVehicles,
    chargesPerWeek,
    averageChargeSize,
    monthlyRequirementKWh,
    onQuoteChange
}) => {
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const numericValue = value === '' ? null : parseFloat(value);

        let updatedValues: any = {};
        if (name === 'numberOfVehicles') {
            updatedValues = { numberOfVehicles: numericValue };
            calculateMonthlyRequirement(numericValue, chargesPerWeek, averageChargeSize);
        } else if (name === 'chargesPerWeek') {
            updatedValues = { chargesPerWeek: numericValue };
            calculateMonthlyRequirement(numberOfVehicles, numericValue, averageChargeSize);
        } else if (name === 'averageChargeSize') {
            updatedValues = { averageChargeSize: numericValue };
            calculateMonthlyRequirement(numberOfVehicles, chargesPerWeek, numericValue);
        }

        onQuoteChange(updatedValues);
    };

    const calculateMonthlyRequirement = (vehicles: number | null, charges: number | null, chargeSize: number | null) => {
        if (vehicles !== null && charges !== null && chargeSize !== null) {
            const result = (vehicles * charges * chargeSize * 52) / 12;
            onQuoteChange({ monthlyRequirementKWh: result });
        } else {
            onQuoteChange({ monthlyRequirementKWh: 0 });
        }
    };

    return (
        <Container maxWidth="md" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <Card elevation={2} sx={{ width: '100%', maxWidth: 600 }}>
                <CardHeader
                    avatar={<TimelineIcon color="primary" />}
                    title="Power Requirements"
                    sx={{
                        backgroundColor: (theme) => theme.palette.grey[50],
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`
                    }}
                />
                <CardContent>
                    <Box component="form" sx={{ '& > :not(style)': { mb: 3 } }}>
                        <TextField
                            fullWidth
                            label="Number of Vehicles"
                            name="numberOfVehicles"
                            type="number"
                            value={numberOfVehicles || ''}
                            onChange={onChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="Charges per Week"
                            name="chargesPerWeek"
                            type="number"
                            value={chargesPerWeek || ''}
                            onChange={onChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            label="Average Charge Size (kW/h)"
                            name="averageChargeSize"
                            type="number"
                            value={averageChargeSize || ''}
                            onChange={onChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle1" color="text.secondary">
                                Monthly Requirement
                            </Typography>
                            <Typography variant="h6">
                                {monthlyRequirementKWh?.toFixed(2)} kW/h
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Container>
    );
}

export default QuickQuotePanel;
